import { CheckCircle2, Circle, CircleDot, MapPin, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/cn';
import { STOP_KIND_BG, STOP_STATUS_BG, countryName } from '@/i18n';
import { formatDuration, formatWhen } from '@/lib/format';
import type { OrderStop } from '@/api/types';
import { Badge } from '@/components/ui/Badge';

const STATUS_ICON = {
  pending: Circle,
  en_route: CircleDot,
  arrived: MapPin,
  completed: CheckCircle2,
  failed: TriangleAlert,
} as const;

const STATUS_TONE = {
  pending: 'text-ink-faint',
  en_route: 'text-info',
  arrived: 'text-accent',
  completed: 'text-ok',
  failed: 'text-danger',
} as const;

/**
 * Хронологията на адресите. Частичната доставка е няколко разтоварвания със
 * собствени състояния — заявката е доставена едва когато всички са изпълнени.
 */
export function StopTimeline({ stops }: { stops: OrderStop[] }) {
  const ordered = [...stops].sort((a, b) => a.sequence - b.sequence);

  return (
    <ol className="relative space-y-4 pl-6">
      <span className="absolute bottom-3 left-[7px] top-3 w-px bg-line" aria-hidden="true" />
      {ordered.map((stop) => {
        const Icon = STATUS_ICON[stop.status];
        return (
          <li key={stop.id} className="relative">
            <Icon
              className={cn('absolute -left-6 top-0.5 size-4 bg-surface', STATUS_TONE[stop.status])}
            />
            <div className="flex flex-wrap items-baseline gap-2">
              <Badge tone={stop.kind === 'pickup' ? 'info' : 'accent'}>{STOP_KIND_BG[stop.kind]}</Badge>
              <span className="font-medium">{stop.name}</span>
              <span className="text-xs text-ink-dim">
                {stop.address}, {stop.postcode ?? ''} {stop.city}, {countryName(stop.country)}
              </span>
              <Badge tone={stop.status === 'failed' ? 'danger' : 'neutral'} className="ml-auto">
                {STOP_STATUS_BG[stop.status]}
              </Badge>
            </div>

            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-dim">
              <span>
                Прозорец: {formatWhen(stop.windowFrom)} – {formatWhen(stop.windowTo)}
                {stop.isHardDeadline ? (
                  <Badge tone="warn" className="ml-1">
                    твърд срок
                  </Badge>
                ) : null}
              </span>
              {stop.arrivedAt ? <span>Пристигнал: {formatWhen(stop.arrivedAt)}</span> : null}
              {stop.completedAt ? <span>Изпълнен: {formatWhen(stop.completedAt)}</span> : null}
              {stop.waitingMinutes ? <span>Престой: {formatDuration(stop.waitingMinutes)}</span> : null}
              {stop.reference ? <span>Реф.: {stop.reference}</span> : null}
              {stop.contactName ? (
                <span>
                  Контакт: {stop.contactName}
                  {stop.contactPhone ? ` · ${stop.contactPhone}` : ''}
                </span>
              ) : null}
            </div>

            {stop.instructions ? (
              <p className="mt-1 text-xs text-ink-dim">Указания: {stop.instructions}</p>
            ) : null}
            {/* Забележка по кл. 18 от ЧМР — без вписване на място доказателството при щета е загубено. */}
            {stop.reservations ? (
              <p className="mt-1 rounded border border-warn/40 bg-warn-soft px-2 py-1 text-xs text-warn">
                Забележки (ЧМР кл. 18): {stop.reservations}
              </p>
            ) : null}
            {stop.failReason ? (
              <p className="mt-1 rounded border border-danger/40 bg-danger-soft px-2 py-1 text-xs text-danger">
                Отказана доставка: {stop.failReason}
              </p>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
